import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Calendar, Phone, Video, XCircle } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function AppointmentManagePage() {
  const { token } = useParams();
  const { t } = useLang();
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/appointments/manage/${token}`)
      .then(({ data }) => setAppt(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  async function cancel() {
    if (!window.confirm(t("appointmentManage.confirmCancel"))) return;
    setCancelling(true);
    try {
      await api.delete(`/appointments/manage/${token}`);
      setAppt((a) => ({ ...a, status: "cancelled" }));
      toast.success(t("appointmentManage.cancelSuccess"));
    } catch {
      toast.error(t("appointmentManage.cancelError"));
    } finally {
      setCancelling(false);
    }
  }

  return (
    <>
      <Header />
      <main className="bg-[var(--bg)] min-h-[70vh]">
        <section className="pt-28 pb-20">
          <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading && (
              <div className="h-64 grid place-items-center">
                <Loader2 size={28} className="animate-spin text-[var(--brand-orange)]" />
              </div>
            )}

            {!loading && notFound && (
              <div className="card-dark p-8 text-center text-white/60" data-testid="appointment-manage-notfound">
                {t("appointmentManage.notFound")}
              </div>
            )}

            {!loading && appt && (
              <div className="card-dark p-8 text-center space-y-4" data-testid="appointment-manage-panel">
                <div className="w-14 h-14 mx-auto grid place-items-center rounded-full bg-[var(--gold)]/15 border border-[var(--gold)]/30">
                  <Calendar size={22} className="text-[var(--gold)]" />
                </div>
                <h1 className="font-display text-2xl font-bold text-white">
                  {appt.status === "cancelled" ? t("appointmentManage.cancelled") : t("appointmentManage.yourConsultation")}
                </h1>
                <p className="text-white/70 text-lg">
                  {new Date(appt.start_time).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
                </p>
                <p className="text-white/50 text-sm flex items-center justify-center gap-1.5">
                  {appt.meeting_preference === "video" ? <Video size={14} /> : <Phone size={14} />}
                  {appt.meeting_preference === "video" ? t("scheduler.videoCall") : t("scheduler.phoneCall")}
                </p>

                {appt.status === "cancelled" ? (
                  <p className="text-white/50 text-sm">{t("appointmentManage.onHoldNote")}</p>
                ) : (
                  <button
                    onClick={cancel}
                    disabled={cancelling}
                    className="btn-outline-gold text-sm py-2 px-5 mx-auto"
                    data-testid="appointment-manage-cancel"
                  >
                    {cancelling ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                    {t("appointmentManage.cancelButton")}
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
